import { Room } from "livekit-client";

/**
 * LiveKit Cloud specific utilities to handle the migration from self-hosted to Cloud
 */

export interface CloudConnectionOptions {
  maxRetries?: number;
  retryDelay?: number;
  stabilityTimeout?: number;
}

/**
 * Validates if LiveKit Cloud connection is stable and ready for media operations
 */
export const validateCloudConnectionStability = async (
  room: Room,
  options: CloudConnectionOptions = {}
): Promise<boolean> => {
  const { stabilityTimeout = 8000 } = options;
  
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('LiveKit Cloud connection stability timeout'));
    }, stabilityTimeout);
    
    const checkStability = () => {
      if (
        room.state === 'connected' && 
        room.localParticipant.identity && 
        !room.isReconnecting &&
        room.engine?.client?.ws?.readyState === WebSocket.OPEN
      ) {
        clearTimeout(timeout);
        resolve(true);
      } else {
        setTimeout(checkStability, 100);
      }
    };
    
    checkStability();
  });
};

/**
 * Safely publishes a track to LiveKit Cloud with connection validation
 */
export const publishTrackSafely = async (
  room: Room,
  track: MediaStreamTrack,
  options: any,
  retryCount: number = 0
): Promise<void> => {
  const maxRetries = 3;
  
  if (!room || room.state !== 'connected' || room.isReconnecting) {
    throw new Error('LiveKit Cloud not ready for track publishing');
  }
  
  try {
    // Small delay to ensure stable connection
    await new Promise(resolve => setTimeout(resolve, 100));
    
    if (room.state !== 'connected') {
      throw new Error('LiveKit Cloud disconnected during publish attempt');
    }
    
    await room.localParticipant.publishTrack(track, options);
  } catch (error) {
    if (
      retryCount < maxRetries &&
      error instanceof Error &&
      /negotiation|timeout|failed to publish/i.test(error.message)
    ) {
      console.warn(`LiveKit Cloud publish retry ${retryCount + 1}/${maxRetries}:`, error.message);
      const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000);
      
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      
      // Validate connection is still stable before retry
      await validateCloudConnectionStability(room, { stabilityTimeout: 3000 });
      
      return publishTrackSafely(room, track, options, retryCount + 1);
    } else {
      throw error;
    }
  }
};

/**
 * Creates exponential backoff delay for LiveKit Cloud reconnections
 */
export const getCloudReconnectDelay = (retryCount: number): number => {
  return Math.min(1000 * Math.pow(2, retryCount), 10000);
};

/**
 * Optimized Room configuration for LiveKit Cloud
 */
export const getCloudOptimizedRoomConfig = (audioConfig?: {
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  sampleRate: number;
}) => {
  console.log("🔊 [getCloudOptimizedRoomConfig] Received audioConfig:", audioConfig);
  
  const finalAudioConfig = audioConfig || {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 48000,
  };
  
  console.log("🔊 [getCloudOptimizedRoomConfig] Final audioConfig being used:", finalAudioConfig);
  
  const config = {
  // LiveKit Cloud optimized settings
  adaptiveStream: true,
  dynacast: true,
  e2eeEnabled: false, // Disable E2EE for better Cloud performance
  
  videoCaptureDefaults: {
    resolution: { width: 960, height: 540 }, // Optimized for Cloud
    facingMode: "user",
  },
  audioCaptureDefaults: finalAudioConfig,
  publishDefaults: {
    videoCodec: "h264" as const, // H.264 more reliable on Cloud than VP9
    audioCodec: "opus" as const,
    screenShareEncoding: {
      maxBitrate: 8_000_000, // Reduced for Cloud stability
      maxFramerate: 30,
    },
    videoEncoding: {
      maxBitrate: 3_000_000, // Reduced for Cloud stability
      maxFramerate: 30,
    },
    // Cloud-specific optimizations
    dtx: true, // Discontinuous Transmission
    red: true, // Redundancy encoding
  },
  // LiveKit Cloud connection optimizations
  reconnectPolicy: {
    nextRetryDelayInMs: (context: any) => getCloudReconnectDelay(context.retryCount),
    maxRetryCount: 5,
  },
  };

  console.log("🔊 [getCloudOptimizedRoomConfig] Final config being returned:", config);
  return config;
};

/**
 * Debounce function for reducing monitoring frequency on Cloud
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Advanced debouncing for connection attempts with state tracking
 */
export const createConnectionDebouncer = (minInterval: number = 2000) => {
  let lastCallTime = 0;
  let pendingTimeout: NodeJS.Timeout | null = null;
  
  return {
    shouldAllow: (): boolean => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallTime;
      
      if (timeSinceLastCall >= minInterval) {
        lastCallTime = now;
        return true;
      }
      return false;
    },
    
    scheduleNext: (callback: () => void): void => {
      if (pendingTimeout) {
        clearTimeout(pendingTimeout);
      }
      
      const now = Date.now();
      const timeSinceLastCall = now - lastCallTime;
      const remainingWait = Math.max(0, minInterval - timeSinceLastCall);
      
      pendingTimeout = setTimeout(() => {
        lastCallTime = Date.now();
        callback();
        pendingTimeout = null;
      }, remainingWait);
    },
    
    cancel: (): void => {
      if (pendingTimeout) {
        clearTimeout(pendingTimeout);
        pendingTimeout = null;
      }
    },
    
    reset: (): void => {
      lastCallTime = 0;
      if (pendingTimeout) {
        clearTimeout(pendingTimeout);
        pendingTimeout = null;
      }
    }
  };
};

/**
 * Check if an error is a LiveKit Cloud negotiation issue that should trigger retry
 */
export const isCloudNegotiationError = (error: Error): boolean => {
  return /negotiation|UnexpectedConnectionState|failed to publish|timeout|connection closed/i.test(error.message);
};

/**
 * Enhanced error handler for LiveKit Cloud specific issues
 * NOTE: This function only categorizes errors. Sentry logging should be done
 * by the caller using logCategorizedError() for better context.
 */
export const handleCloudError = (error: Error, context: string) => {
  if (isCloudNegotiationError(error)) {
    console.warn(`[LiveKit Cloud] Negotiation issue in ${context}:`, error.message);
    return 'negotiation_error';
  } else if (error.message.includes('region')) {
    console.warn(`[LiveKit Cloud] Region connectivity issue in ${context}:`, error.message);
    return 'region_error';
  } else if (error.message.includes('bandwidth')) {
    console.warn(`[LiveKit Cloud] Bandwidth issue in ${context}:`, error.message);
    return 'bandwidth_error';
  } else {
    console.error(`[LiveKit Cloud] Unexpected error in ${context}:`, error);
    return 'unknown_error';
  }
};